<template>
  <div class="mx-auto flex w-full max-w-[800px] flex-col transition-all">
    <div
      v-for="(item, i) in items"
      :key="i"
      class="mb-[18px] flex flex-col rounded-lg bg-white shadow-[0px_0px_35px_rgba(0,0,0,0.08)] last:mb-0"
    >
      <button
        type="button"
        class="relative flex w-full items-center gap-2 py-3.5 pl-6 pr-12 text-left lg:pr-14"
        :aria-expanded="isOpen(i)"
        @click.stop.prevent="toggle(i)"
      >
        <h2 class="m-0 text-base font-medium text-black">
          {{ item.question }}
        </h2>
        <span class="sr-only">
          <Translate translate-key="faq_inner.toggle_action" />
        </span>
        <span
          class="absolute right-3.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-[#dfe4ef] lg:right-6"
          aria-hidden="true"
        >
          <span class="block h-[1.5px] w-3.5 shrink-0 bg-white" />
          <span
            class="absolute block h-3.5 w-[1.5px] bg-white transition-opacity"
            :class="{ 'opacity-0': isOpen(i) }"
          />
        </span>
      </button>
      <!-- SEO: answer is always in DOM (no v-if/v-show) so crawlers can index FAQ content -->
      <div
        class="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        :class="isOpen(i) ? 'max-h-[2000px]' : 'max-h-0'"
      >
        <div class="border-t border-[#dfe4ef] text-sm leading-normal">
          <div
            class="prose prose-gray max-w-none py-1 px-6 pb-4 pt-2 text-sm lg:px-8 prose-p:mb-2 prose-p:last:mb-0 prose-ul:my-2 prose-ul:ml-4 prose-ul:list-disc prose-ul:pl-4 prose-ul:pb-4 prose-ul:pt-0 prose-ol:my-2 prose-ol:ml-4 prose-ol:pl-4 prose-ol:pb-4 prose-ol:pt-0 prose-li:mb-1 prose-li:last:mb-0 prose-a:text-success prose-a:underline prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-gray-700 prose-th:px-1 prose-th:py-0.5 prose-td:border prose-td:border-gray-700 prose-td:px-1 prose-td:py-0.5"
            v-html="item.answer"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import {
  FAQ_TYPE_CLIENT,
  FAQ_TYPE_CARDHOLDER,
  FAQ_KEY_BANKS,
} from "../constants";
import { useI18n } from "vue-i18n";
import { useRuntimeConfig } from "#app";

const props = withDefaults(
  defineProps<{
    faqType?: number;
  }>(),
  { faqType: FAQ_TYPE_CLIENT },
);

const { t } = useI18n();
const config = useRuntimeConfig();
const activeQuestionIndex = ref<number | null>(null);
/** Only apply open state after client mount to avoid hydration mismatch breaking clicks in production */
const clientReady = ref(false);
onMounted(() => {
  clientReady.value = true;
});
const isOpen = (index: number) =>
  clientReady.value && activeQuestionIndex.value === index;
const banks = ref<{ name: string }[] | null>(null);
const isBanksLoading = ref(false);

const cardLimitVal = ref("");
const cardAgeValDisplay = ref("");

const faqData = computed(() =>
  Array.from({ length: 20 }, (_, i) => ({
    question: t(`faq_inner.faq_data.${i + 1}.title`),
    answer: t(`faq_inner.faq_data.${i + 1}.answer`),
  })),
);

const faqDataCardholder = computed(() =>
  Array.from({ length: 13 }, (_, i) => {
    const key = i + 1 === 8 ? FAQ_KEY_BANKS : undefined;
    const answer =
      i + 1 === 8
        ? t("faq_inner.faq_data_cardholder.8.answer", {
            banksList: banksList.value,
            cardLimitVal: cardLimitVal.value || "—",
            cardAgeValDisplay: cardAgeValDisplay.value || "—",
          })
        : t(`faq_inner.faq_data_cardholder.${i + 1}.answer`);
    return {
      question: t(`faq_inner.faq_data_cardholder.${i + 1}.title`),
      answer,
      key,
    };
  }),
);

const items = computed(() =>
  props.faqType === FAQ_TYPE_CARDHOLDER
    ? faqDataCardholder.value
    : faqData.value,
);

const banksList = computed(() => {
  if (isBanksLoading.value) return t("faq_inner.loading");
  if (banks.value === null) return "";
  if (banks.value?.length) return banks.value.map((b) => b.name).join(", ");
  return "";
});

async function fetchBanks() {
  isBanksLoading.value = true;
  try {
    const data = await $fetch<{ name: string }[]>(
      `${config.public.baseURL}v1/public/banks/`,
      { params: { is_active: true } },
    );
    banks.value = Array.isArray(data) ? data : [];
  } catch {
    banks.value = [];
  } finally {
    isBanksLoading.value = false;
  }
}

function toggle(index: number) {
  const item = items.value[index];
  if (activeQuestionIndex.value === index) {
    activeQuestionIndex.value = null;
    return;
  }
  activeQuestionIndex.value = index;
  if (props.faqType === FAQ_TYPE_CARDHOLDER && item?.key === FAQ_KEY_BANKS) {
    fetchBanks();
  }
}
</script>
